/**
 * The ending.
 *
 * A LIFE deliberately refuses a single score. Instead the run is measured on
 * eight independent dimensions, and an *archetype* is inferred from the shape
 * of those dimensions plus what the life actually contained. The archetype is
 * an interpretation, never a class the player picked.
 */

import type {
  EndingDimension,
  EndingReport,
  LogEntry,
  PlayerState,
} from './types';
import {
  activeRegretWeight,
  clamp100,
  financialSecurity,
  netWorth,
  ramp,
  socialHealth,
} from './derived';
import { ARCHETYPE_DEF_LIST, ASPIRATIONS } from './content';
import { evaluateAll } from './conditions';
import { Rng } from './rng';

/* ------------------------------------------------------------------ *
 * Dimensions
 * ------------------------------------------------------------------ */

function financialScore(state: PlayerState): number {
  const nw = netWorth(state);
  const peak = state.finance.peakNetWorth;
  const wealth = ramp(Math.log10(Math.max(1, Math.max(nw, peak * 0.5))), 3.2, 6.4) * 70;
  const security = (financialSecurity(state) / 100) * 30;
  return clamp100(wealth + security);
}

/** Mean value of a running-total counter over the years actually lived. */
export function lifetimeMean(state: PlayerState, key: string, fallback: number): number {
  const years = state.counters['yearsLived'] ?? 0;
  if (years <= 0) return fallback;
  return (state.counters[key] ?? 0) / years;
}

function healthScore(state: PlayerState): number {
  const lifespanBonus = ramp(state.age, 62, 92) * 34;
  const lifetimeHealth = lifetimeMean(state, 'healthSum', state.stats.health);
  return clamp100(lifetimeHealth * 0.5 + lifespanBonus + (state.habits.exercise / 100) * 16);
}

function relationshipScore(state: PlayerState): number {
  const social = socialHealth(state);
  const familyBonus = Math.min(18, state.family.childrenBorn * 7);
  const unionBonus =
    state.family.maritalStatus === 'married' || state.family.maritalStatus === 'partnered' ? 12 : 0;
  const longUnion = state.family.unionSinceAge !== null ? Math.min(12, (state.age - state.family.unionSinceAge) / 3) : 0;
  const churn = Math.min(14, state.family.divorces * 7);
  return clamp100(social * 0.62 + familyBonus + unionBonus + longUnion - churn);
}

function purposeScore(state: PlayerState): number {
  const contributions = Math.min(20, (state.counters['contributions'] ?? 0) * 3.5);
  const works = Math.min(14, (state.counters['creativeWorks'] ?? 0) * 3.5);
  const dreams = state.aspirations.filter((a) => a.kind === 'dream' && a.status === 'achieved').length;
  const lifetimePurpose = lifetimeMean(state, 'purposeSum', state.stats.purpose);
  return clamp100(lifetimePurpose * 0.55 + contributions + works + Math.min(18, dreams * 6));
}

function growthScore(state: PlayerState): number {
  const skills = Object.values(state.skills);
  const best = Math.max(...skills);
  const breadth = skills.filter((s) => s >= 45).length;
  const credentials = Math.min(12, state.education.credentials.length * 4);
  return clamp100(best * 0.45 + breadth * 5 + credentials + (state.habits.learning / 100) * 14);
}

function legacyScore(state: PlayerState): number {
  const contributions = Math.min(26, (state.counters['contributions'] ?? 0) * 4);
  const children = Math.min(20, state.family.childrenBorn * 8);
  const works = Math.min(16, (state.counters['creativeWorks'] ?? 0) * 4);
  const wealthTransfer = ramp(Math.log10(Math.max(1, netWorth(state))), 4, 6.5) * 18;
  const achievements = Math.min(20, state.achievements.length * 2.5);
  return clamp100(contributions + children + works + wealthTransfer + achievements);
}

function resilienceScore(state: PlayerState): number {
  const crises = state.counters['crises'] ?? 0;
  const recovered = state.burnout.episodes > 0 && state.burnout.stage !== 'burntOut' ? 16 : 0;
  const rebuilt = state.flags['wasBankrupt'] && netWorth(state) > 0 ? 22 : 0;
  const survivedWell = crises > 0 ? Math.min(30, crises * 9) * (state.stats.happiness / 100 + 0.35) : 0;
  return clamp100(
    (state.traits.resilience / 100) * 34 + recovered + rebuilt + survivedWell + (state.stats.happiness / 100) * 14,
  );
}

function happinessScore(state: PlayerState): number {
  const lifetime = lifetimeMean(state, 'happinessSum', state.stats.happiness);
  return clamp100(lifetime * 0.8 + (100 - Math.min(100, activeRegretWeight(state))) * 0.2);
}

export function endingDimensions(state: PlayerState): EndingDimension[] {
  const financial = financialScore(state);
  const health = healthScore(state);
  const relationships = relationshipScore(state);
  const purpose = purposeScore(state);
  const happiness = happinessScore(state);
  const growth = growthScore(state);
  const legacy = legacyScore(state);
  const resilience = resilienceScore(state);

  const note = (v: number, low: string, mid: string, high: string): string =>
    v < 38 ? low : v < 68 ? mid : high;

  return [
    {
      key: 'financial',
      label: 'Financial',
      score: financial,
      note: note(financial, 'Money was always a constraint.', 'Comfortable enough, never abundant.', 'Money stopped being a question.'),
    },
    {
      key: 'health',
      label: 'Health',
      score: health,
      note: note(health, 'The body was not looked after.', 'Broadly well, with some cost.', 'Strong for a long time.'),
    },
    {
      key: 'relationships',
      label: 'Relationships',
      score: relationships,
      note: note(relationships, 'Often alone.', 'A few people, held loosely.', 'Surrounded, and genuinely known.'),
    },
    {
      key: 'purpose',
      label: 'Purpose',
      score: purpose,
      note: note(purpose, 'Rarely sure what it was for.', 'Purpose came and went.', 'Knew why they were doing it.'),
    },
    {
      key: 'happiness',
      label: 'Happiness',
      score: happiness,
      note: note(happiness, 'Hard years outnumbered good ones.', 'Mostly content, sometimes not.', 'A happy life by their own measure.'),
    },
    {
      key: 'growth',
      label: 'Growth',
      score: growth,
      note: note(growth, 'Stopped learning early.', 'Kept developing, unevenly.', 'Became genuinely capable.'),
    },
    {
      key: 'legacy',
      label: 'Legacy',
      score: legacy,
      note: note(legacy, 'Left little behind deliberately.', 'Left something, to some people.', 'Left a great deal behind.'),
    },
    {
      key: 'resilience',
      label: 'Resilience',
      score: resilience,
      note: note(resilience, 'Setbacks stuck.', 'Recovered, slowly.', 'Absorbed a great deal and stood up.'),
    },
  ];
}

/* ------------------------------------------------------------------ *
 * Archetype scoring
 * ------------------------------------------------------------------ */

function resolveScorePath(state: PlayerState, dims: Record<string, number>, path: string): number {
  if (path.startsWith('ending.')) {
    const key = path.slice(7);
    if (key === 'balance') {
      // Balance rewards a high floor, not a high mean.
      const values = Object.values(dims);
      const min = Math.min(...values);
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      return clamp100(min * 0.65 + mean * 0.35);
    }
    if (key === 'regret') return Math.min(100, activeRegretWeight(state));
    if (key === 'lateGrowth') {
      // Did the final third contain the best material?
      const cutoff = state.startAge + (state.age - state.startAge) * 0.66;
      const late = state.log.filter((l) => l.age >= cutoff && l.weight >= 6 && l.tone !== 'negative').length;
      const early = state.log.filter((l) => l.age < cutoff && l.weight >= 6 && l.tone !== 'negative').length;
      const lateChanges = state.counters['lateChanges'] ?? 0;
      return clamp100(late * 12 + lateChanges * 18 - early * 5);
    }
    return dims[key] ?? 0;
  }
  if (path.startsWith('counter.')) return state.counters[path.slice(8)] ?? 0;
  if (path.startsWith('flag.')) return state.flags[path.slice(5)] ? 1 : 0;
  if (path.startsWith('trait.')) return state.traits[path.slice(6) as keyof typeof state.traits] ?? 0;
  if (path.startsWith('habit.')) return state.habits[path.slice(6) as keyof typeof state.habits] ?? 0;
  if (path === 'skill.max') return Math.max(...Object.values(state.skills));
  if (path.startsWith('skill.')) return state.skills[path.slice(6) as keyof typeof state.skills] ?? 0;
  if (path === 'career.promotions') return state.career.promotions;
  if (path === 'career.pivots') return state.career.pivots;
  if (path === 'career.layoffs') return state.career.layoffs;
  if (path === 'career.reputation') return state.career.reputation;
  if (path === 'career.roleLevel') return state.career.roleLevel;
  if (path === 'business.exits') return state.business.exits;
  if (path === 'business.failures') return state.business.failures;
  if (path === 'family.childrenBorn') return state.family.childrenBorn;
  if (path === 'finance.bankruptcies') return state.counters['bankruptcies'] ?? 0;
  if (path === 'finance.lifetimeEarnings.log') return Math.log10(Math.max(1, state.finance.lifetimeEarnings));
  if (path === 'inertia.decayedCount') return state.inertia.decayedCount;
  if (path === 'inertia.deferredCount') return state.inertia.deferredCount;
  if (path === 'age') return state.age;
  return 0;
}

/**
 * Plausible ceiling for each score path, used to normalise terms to 0–1.
 *
 * Without normalisation an archetype whose terms happen to be large numbers
 * (net worth, reputation) outscores every other archetype in every life, and
 * the interpretive layer collapses into two labels. Dividing by a realistic
 * maximum makes the weights mean what they look like they mean.
 */
const SCORE_CEILING: Record<string, number> = {
  'counter.creativeWorks': 8,
  'counter.contributions': 10,
  'counter.travelTrips': 8,
  'counter.crises': 5,
  'counter.lateChanges': 3,
  'counter.luckyBreaks': 5,
  'career.promotions': 6,
  'career.pivots': 4,
  'career.layoffs': 4,
  'career.reputation': 100,
  'career.roleLevel': 4,
  'business.exits': 2,
  'business.failures': 3,
  'family.childrenBorn': 4,
  'finance.bankruptcies': 2,
  'finance.lifetimeEarnings.log': 7.5,
  'inertia.decayedCount': 8,
  'inertia.deferredCount': 20,
  'skill.max': 100,
  age: 100,
};

function ceilingFor(path: string): number {
  if (path.startsWith('ending.')) return 100;
  if (path.startsWith('trait.') || path.startsWith('habit.') || path.startsWith('skill.')) return 100;
  if (path.startsWith('flag.')) return 1;
  return SCORE_CEILING[path] ?? 10;
}

export function scoreArchetypes(
  state: PlayerState,
  dims: Record<string, number>,
): { id: string; score: number }[] {
  const scored: { id: string; score: number }[] = [];
  for (const def of ARCHETYPE_DEF_LIST) {
    if (def.requires && !evaluateAll(state, def.requires)) continue;
    let total = 0;
    let weightSum = 0;
    for (const term of def.score) {
      const raw = resolveScorePath(state, dims, term.path);
      const normalised = Math.min(1, Math.max(0, raw / ceilingFor(term.path)));
      total += normalised * term.weight;
      weightSum += Math.abs(term.weight);
    }
    scored.push({ id: def.id, score: weightSum > 0 ? (total / weightSum) * 100 : 0 });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored;
}

/* ------------------------------------------------------------------ *
 * Report
 * ------------------------------------------------------------------ */

/** Pick the entries that tell the story, capped so the timeline stays readable. */
export function buildTimeline(state: PlayerState, limit = 26): LogEntry[] {
  const ranked = [...state.log]
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => b.entry.weight - a.entry.weight || a.index - b.index)
    .slice(0, limit)
    .sort((a, b) => a.entry.age - b.entry.age || a.index - b.index);
  return ranked.map((r) => r.entry);
}

function headlineFor(state: PlayerState, dims: Record<string, number>): string {
  const name = state.identity.name;
  const wealthy = (dims['financial'] ?? 0) >= 68;
  const happy = (dims['happiness'] ?? 0) >= 62;
  const connected = (dims['relationships'] ?? 0) >= 62;
  const purposeful = (dims['purpose'] ?? 0) >= 62;

  if (wealthy && !happy) return `${name} built wealth and did not quite arrive at a good life.`;
  if (!wealthy && happy && connected) return `${name} never got rich and was, by every measure that matters, fine.`;
  if (wealthy && happy && connected) return `${name} managed the rare thing: money, people, and time to enjoy both.`;
  if (purposeful && !wealthy) return `${name} chose meaning over money, knowingly, and it held up.`;
  if (!happy && !connected) return `${name} worked hard, and much of it was lonely.`;
  return `${name} lived a life with real weight in it.`;
}

export function buildEndingReport(state: PlayerState): EndingReport {
  const dimensions = endingDimensions(state);
  const dims: Record<string, number> = {};
  for (const d of dimensions) dims[d.key] = d.score;

  const ranked = scoreArchetypes(state, dims);
  const topId = ranked[0]?.id ?? 'arch.balanced';
  const def = ARCHETYPE_DEF_LIST.find((a) => a.id === topId) ?? ARCHETYPE_DEF_LIST[0];

  const rng = Rng.fromState(state.rng).keyed('ending');
  const epitaph = def ? (rng.pick(def.epitaphs) ?? 'A life.') : 'A life.';

  const highlights = state.log
    .filter((l) => l.tone === 'milestone' || (l.tone === 'positive' && l.weight >= 6))
    .slice(-8)
    .map((l) => `${l.age}: ${l.title}`);
  const setbacks = state.log
    .filter((l) => l.tone === 'negative' && l.weight >= 6)
    .slice(-8)
    .map((l) => `${l.age}: ${l.title}`);
  // Aspirations, open intentions and regrets often describe the same gap in
  // three different voices. Report each gap once.
  const normalise = (text: string): string =>
    text
      .toLowerCase()
      .replace(/^(never|meant to|build|reach|keep|get|see|make|leave|own|raise|clear)\s+/g, '')
      .replace(/[^a-z]/g, '')
      .slice(0, 14);
  const unfinished: string[] = [];
  const seenGaps = new Set<string>();
  const addGap = (label: string): void => {
    const key = normalise(label);
    if (key.length === 0 || seenGaps.has(key)) return;
    seenGaps.add(key);
    unfinished.push(label);
  };
  for (const a of state.aspirations) {
    if (a.status === 'active' || a.status === 'failed') addGap(ASPIRATIONS[a.defId]?.label ?? a.defId);
  }
  for (const i of state.inertia.intentions) addGap(i.label);
  for (const r of state.regrets) if (!r.resolved) addGap(r.label);
  unfinished.splice(8);

  return {
    age: state.age,
    cause: state.causeOfDeath ?? 'old age',
    archetypeId: topId,
    archetypeLabel: def?.label ?? 'A Life',
    archetypeNarrative: (def?.narrative ?? '').replace(/\{name\}/g, state.identity.name),
    dimensions,
    timeline: buildTimeline(state),
    headline: headlineFor(state, dims),
    netWorth: netWorth(state),
    legacyScore: Math.round(dims['legacy'] ?? 0),
    epitaph,
    stats: { ...state.stats },
    highlights,
    setbacks,
    unfinished,
  };
}
